/**
 * DocumentDropZone - Step 2 of Quick Create
 *
 * Provides drag-and-drop file upload with:
 * - Auto-categorization from filename
 * - Category override capability
 * - Quick attach from Evidence Vault
 * - Upload progress tracking
 */

import { useState, useCallback, useRef } from 'react';
import { Timestamp, doc, collection } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, auth } from '@/lib/firebase';
import { dualWriteJobDocument } from '@/lib/firestore-helpers';
import { validateFile } from '@/lib/security';
import { sanitizeFileName } from '@/lib/sanitize';
import {
  detectDocumentCategory,
  getCategoryLabel,
  getDocumentCategories,
} from '@/lib/document-category-detector';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Upload,
  FileUp,
  X,
  FileText,
  Image,
  Loader2,
  ArrowRight,
  Vault,
  Sparkles,
  AlertCircle,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { AttachVaultDocumentsDialog } from '@/components/job-detail/AttachVaultDocumentsDialog';
import type { UploadedDocument } from '@/pages/QuickCreate';
import { cn } from '@/lib/utils';

interface DocumentDropZoneProps {
  jobId: string;
  contractorId: string;
  permitType: string;
  existingDocuments: UploadedDocument[];
  onComplete: (documents: UploadedDocument[]) => void;
  onBack: () => void;
}

interface PendingFile {
  file: File;
  id: string;
  category: string;
  categoryLabel: string;
  autoDetected: boolean;
  confidence: number;
  uploading: boolean;
  uploaded: boolean;
  error?: string;
}

// File validation constants
const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/tiff',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx', '.webp', '.tiff', '.tif'];
const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB

export function DocumentDropZone({
  jobId,
  contractorId,
  permitType,
  existingDocuments,
  onComplete,
  onBack,
}: DocumentDropZoneProps) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // State
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [uploadedDocs, setUploadedDocs] = useState<UploadedDocument[]>(existingDocuments);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showVaultDialog, setShowVaultDialog] = useState(false);

  // Generate unique ID for pending files
  const generateId = () => `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Handle file selection (from drop or click)
  const handleFiles = useCallback((files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const newPendingFiles: PendingFile[] = [];
    const errors: string[] = [];

    for (const file of fileArray) {
      // Validate file
      const validation = validateFile(file, {
        maxSizeBytes: MAX_FILE_SIZE,
        allowedTypes: ALLOWED_FILE_TYPES,
        allowedExtensions: ALLOWED_EXTENSIONS,
      });

      if (!validation.valid) {
        errors.push(`${file.name}: ${validation.error}`);
        continue;
      }

      // Auto-detect category
      const detection = detectDocumentCategory(file.name);

      newPendingFiles.push({
        file,
        id: generateId(),
        category: detection.category || '',
        categoryLabel: detection.categoryLabel || '',
        autoDetected: detection.category !== null,
        confidence: detection.confidence,
        uploading: false,
        uploaded: false,
      });
    }

    if (errors.length > 0) {
      toast({
        title: 'Some files rejected',
        description: errors.join('; '),
        variant: 'destructive',
      });
    }

    if (newPendingFiles.length > 0) {
      setPendingFiles((prev) => [...prev, ...newPendingFiles]);
    }
  }, [toast]);

  // Drag handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if we're leaving the drop zone entirely
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles]);

  // Remove pending file
  const removePendingFile = (id: string) => {
    setPendingFiles((prev) => prev.filter((f) => f.id !== id));
  };

  // Update file category
  const updateFileCategory = (id: string, category: string) => {
    setPendingFiles((prev) =>
      prev.map((f) =>
        f.id === id
          ? {
              ...f,
              category,
              categoryLabel: getCategoryLabel(category),
              autoDetected: false,
            }
          : f
      )
    );
  };

  // Upload all pending files
  const uploadFiles = async () => {
    if (pendingFiles.length === 0) return;

    const user = auth.currentUser;
    if (!user) {
      toast({
        title: 'Not authenticated',
        description: 'Please sign in to upload documents',
        variant: 'destructive',
      });
      return;
    }

    setIsUploading(true);

    const newUploadedDocs: UploadedDocument[] = [];

    for (const pending of pendingFiles) {
      // Mark as uploading
      setPendingFiles((prev) =>
        prev.map((f) => (f.id === pending.id ? { ...f, uploading: true } : f))
      );

      try {
        // Sanitize filename and upload
        const safeFileName = sanitizeFileName(pending.file.name);
        const storagePath = `job-documents/${jobId}/${Date.now()}-${safeFileName}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, pending.file);
        const downloadURL = await getDownloadURL(storageRef);

        // Save document metadata
        const documentId = doc(collection(db, 'job_documents')).id;
        await dualWriteJobDocument(contractorId, jobId, documentId, {
          uploaded_by: user.uid,
          document_name: safeFileName,
          document_url: downloadURL,
          document_type: pending.category ? 'compliance_doc' : 'other',
          description: '',
          category: pending.category || null,
          complianceCategory: pending.category || null,
          status: pending.category ? 'uploaded' : null,
          createdAt: Timestamp.now(),
          uploadedViaQuickCreate: true,
        });

        // Add to uploaded docs
        newUploadedDocs.push({
          id: documentId,
          name: safeFileName,
          category: pending.category,
          categoryLabel: pending.categoryLabel || 'Other',
          size: pending.file.size,
          url: downloadURL,
          uploadedAt: new Date(),
          source: 'upload',
        });

        // Mark as uploaded
        setPendingFiles((prev) =>
          prev.map((f) => (f.id === pending.id ? { ...f, uploading: false, uploaded: true } : f))
        );
      } catch (error: any) {
        console.error('Upload error:', error);
        setPendingFiles((prev) =>
          prev.map((f) =>
            f.id === pending.id ? { ...f, uploading: false, error: error.message } : f
          )
        );
      }
    }

    // Update state with new docs
    setUploadedDocs((prev) => [...prev, ...newUploadedDocs]);

    // Clear successfully uploaded files
    setPendingFiles((prev) => prev.filter((f) => !f.uploaded));

    setIsUploading(false);

    if (newUploadedDocs.length > 0) {
      toast({
        title: 'Upload complete',
        description: `${newUploadedDocs.length} document${newUploadedDocs.length !== 1 ? 's' : ''} uploaded`,
      });
    }
  };

  // Handle vault document attachment
  const handleVaultComplete = () => {
    setShowVaultDialog(false);
    // Vault dialog will have written documents to Firestore
    // We could refetch here, but for now just continue
    toast({
      title: 'Documents attached',
      description: 'Vault documents have been attached to this permit',
    });
  };

  // Remove uploaded document
  const removeUploadedDoc = (id: string) => {
    setUploadedDocs((prev) => prev.filter((d) => d.id !== id));
  };

  // Get file icon
  const getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return <FileText className="h-4 w-4 text-red-500" />;
    if (['jpg', 'jpeg', 'png', 'webp', 'tiff', 'tif'].includes(ext || '')) {
      return <Image className="h-4 w-4 text-blue-500" />;
    }
    return <FileText className="h-4 w-4 text-gray-500" />;
  };

  // Count total documents
  const totalDocs = uploadedDocs.length + pendingFiles.filter((f) => f.uploaded).length;

  return (
    <div className="space-y-6">
      {/* Drop Zone */}
      <Card
        className={cn(
          'transition-colors cursor-pointer',
          isDragging ? 'border-primary border-2 bg-primary/5' : 'border-dashed'
        )}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <CardContent className="py-12">
          <div className="flex flex-col items-center justify-center text-center">
            <div
              className={cn(
                'w-16 h-16 rounded-full flex items-center justify-center mb-4',
                isDragging ? 'bg-primary/20' : 'bg-muted'
              )}
            >
              <Upload
                className={cn('h-8 w-8', isDragging ? 'text-primary' : 'text-muted-foreground')}
              />
            </div>
            <h3 className="text-lg font-semibold mb-1">
              {isDragging ? 'Drop files here' : 'Drag & drop files'}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              or click to browse your computer
            </p>
            <p className="text-xs text-muted-foreground">
              Supported: PDF, JPG, PNG, DOC, DOCX (max 25MB each)
            </p>
          </div>
        </CardContent>
      </Card>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.webp,.tiff,.tif"
        className="hidden"
        onChange={(e) => e.target.files && handleFiles(e.target.files)}
      />

      {/* Quick Attach from Vault */}
      <Card>
        <CardContent className="py-4">
          <Button
            variant="outline"
            className="w-full justify-start gap-3"
            onClick={() => setShowVaultDialog(true)}
          >
            <Vault className="h-5 w-5" />
            <div className="text-left">
              <div className="font-medium">Attach from Evidence Vault</div>
              <div className="text-xs text-muted-foreground">
                Quickly attach your stored licenses & insurance
              </div>
            </div>
          </Button>
        </CardContent>
      </Card>

      {/* Pending Files */}
      {pendingFiles.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileUp className="h-5 w-5" />
              Files to Upload ({pendingFiles.length})
            </CardTitle>
            <CardDescription>
              Review auto-detected categories or select manually
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {pendingFiles.map((pending) => (
              <div
                key={pending.id}
                className={cn(
                  'flex items-center gap-3 p-3 rounded-lg border',
                  pending.error ? 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/30' : 'bg-muted/50'
                )}
              >
                {getFileIcon(pending.file.name)}

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{pending.file.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {(pending.file.size / 1024).toFixed(0)} KB
                    {pending.autoDetected && pending.confidence > 0 && (
                      <span className="ml-2 text-primary">
                        <Sparkles className="h-3 w-3 inline mr-1" />
                        Auto-detected
                      </span>
                    )}
                  </p>
                  {pending.error && (
                    <p className="text-xs text-red-600 flex items-center gap-1 mt-1">
                      <AlertCircle className="h-3 w-3" />
                      {pending.error}
                    </p>
                  )}
                </div>

                <Select
                  value={pending.category || 'none'}
                  onValueChange={(v) => updateFileCategory(pending.id, v === 'none' ? '' : v)}
                  disabled={pending.uploading}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Category</SelectItem>
                    {getDocumentCategories().map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {pending.uploading ? (
                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                ) : (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      removePendingFile(pending.id);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}

            {/* Upload Button */}
            <Button
              onClick={uploadFiles}
              disabled={isUploading || pendingFiles.every((f) => f.uploaded)}
              className="w-full mt-4"
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload {pendingFiles.length} File{pendingFiles.length !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Uploaded Documents */}
      {uploadedDocs.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              Uploaded Documents ({uploadedDocs.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {uploadedDocs.map((doc) => (
              <div key={doc.id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
                {getFileIcon(doc.name)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{doc.name}</p>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {doc.categoryLabel}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      via {doc.source === 'vault' ? 'Vault' : 'Upload'}
                    </span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => removeUploadedDoc(doc.id)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Continue Button */}
      <Card className="border-primary/50 bg-primary/5">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">
                {totalDocs === 0
                  ? 'No documents uploaded yet'
                  : `${totalDocs} document${totalDocs !== 1 ? 's' : ''} ready`}
              </p>
              <p className="text-xs text-muted-foreground">
                Continue to run compliance verification
              </p>
            </div>
            <Button onClick={() => onComplete(uploadedDocs)} disabled={pendingFiles.some((f) => f.uploading)}>
              Continue
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Vault Dialog */}
      <AttachVaultDocumentsDialog
        open={showVaultDialog}
        onOpenChange={setShowVaultDialog}
        jobId={jobId}
        jobAddress=""
        contractorId={contractorId}
        onComplete={handleVaultComplete}
        onSkip={() => setShowVaultDialog(false)}
      />
    </div>
  );
}
